import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import DisablerContext from "@insite/mobius/utilities/DisablerContext";
import getProp from "@insite/mobius/utilities/getProp";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import safeColor from "@insite/mobius/utilities/safeColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import loadingImage from "@rightstore/Images/LoadingImage";
import * as React from "react";
import styled, { keyframes, withTheme } from "styled-components";

interface LoadingOverlayPresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<LoadingOverlayProps>;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
}

export type LoadingOverlayProps = MobiusStyledComponentProps<
    "div",
    LoadingOverlayPresentationProps & {
        /** CSS string or styled-components function to be injected into this component. */
        /** Show/hide status of the backdrop and spinner. */
        loading?: boolean;
        /** Props to be passed to the inner LoadingSpinner component. */
        spinnerProps?: any;
    }
>;

const LoadingOverlayStyle = styled.div<Pick<LoadingOverlayProps, "css">>`
    position: relative;
    & > div:first-child {
        height: 100%;
        width: 100%;
        background: #FFFFFF;       
        ${injectCss}
        &[data-loading="true"] {
            opacity: 1;
            position: absolute;
            z-index: ${getProp("theme.zIndex.loadingOverlay")};
        }
        &[data-loading="false"] {
            position: fixed;
            display: none;
        }
    }
`;

const SpinnerWrapper = styled.div``;

const Loading = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: 15px;
    justify-content: center;     

    & > div:nth-child(1) {
        animation-delay: 0.2s;
    }

    & > div:nth-child(2) {
        animation-delay: 0.4s;
    }

    & > div:nth-child(3) {
        animation-delay: 0.6s;
    }

    & > div:nth-child(4) {
        animation-delay: 0.8s;
    }
`;

const breatheAnimation = keyframes`
 0% { opacity: 0; }
 70% { opacity: 1; }
 100% { opacity: 0; }
`

const Dot = styled.div`
    width: 10px;
    height: 10px;
    margin: 0 10px;
    border-radius: 50%;
    background-color: #003745;
    opacity: 0;
    animation: ${breatheAnimation} 2s ease-in-out infinite;
`;

const progressBarAnimation = keyframes`
    0% { width: 0% }
    100% { width: 100%; }
`;

const ProgressBarContainer = styled.div`
    width: 100vw;
    height: 5px;
    position: absolute;
    top: 0;
    left: 0;
`;

const ProgressBar = styled.div`
    animation: ${progressBarAnimation} 2s ease-in-out;
    animation-iteration-count: 1;
    background: #006FF8;    
    height: 5px;
`;


/**
 * Adds a layer over the content to indicate that it is loading.
 */
const LoadingOverlayRightStore: React.FC<LoadingOverlayProps> = withTheme(({ loading = true, ...props }) => {
    const { children, mergeCss, spinnerProps, theme, ...otherProps } = props;
    const { applyStyledProp } = applyPropBuilder(props, { component: "loadingOverlay" });
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.loadingOverlay?.defaultProps?.mergeCss;

    return (
        <LoadingOverlayStyle css={applyStyledProp("css", resolvedMergeCss)} {...otherProps}>
            <div data-loading={loading} data-test-selector="loadingOverlaySpinner" role="alert" aria-live="assertive">
                <VisuallyHidden>
                    {loading ? theme.translate("loading content") : theme.translate("content loaded")}
                </VisuallyHidden>
                <ProgressBarContainer>
                    <ProgressBar></ProgressBar>
                </ProgressBarContainer>
                <SpinnerWrapper>                    
                    <img src={loadingImage} alt="loadingRightStore" /> 
                    <Loading>
                        <Dot></Dot>
                        <Dot></Dot>
                        <Dot></Dot>
                        <Dot></Dot>
                    </Loading>                   
                </SpinnerWrapper>
            </div>
            <DisablerContext.Provider value={{ disable: loading }}>{children}</DisablerContext.Provider>
        </LoadingOverlayStyle>
    );
});

/** @component */
export default LoadingOverlayRightStore;

export { LoadingOverlayRightStore };
